import { roundUsd } from "./manufacturerPayablesMath.js";

export type ConsumptionIssueInput = {
  mfg_unit_id: string | null;
  material_label: string;
  extended_cost_usd: number;
  notes: string;
};

export type ConsumptionUnitInput = {
  id: string;
  serial: string | null;
  build_completed_at: string;
};

export type ConsumptionSerialRow = {
  unit_id: string;
  serial: string;
  completed_on: string;
  materials_usd: number;
  why: string;
};

export type ConsumptionReport = {
  period_start: string;
  period_end: string;
  serial_count: number;
  total_usd: number;
  rows: ConsumptionSerialRow[];
};

export function emptyConsumptionReport(period_start: string, period_end: string): ConsumptionReport {
  return { period_start, period_end, serial_count: 0, total_usd: 0, rows: [] };
}

function completedOn(iso: string): string {
  return iso.slice(0, 10);
}

function serialLabel(serial: string | null, unitId: string): string {
  const t = (serial ?? "").trim();
  return t || unitId.slice(0, 8);
}

function whyFromIssues(issues: ConsumptionIssueInput[]): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const i of issues) {
    const label = i.material_label.trim() || "material";
    const note = i.notes.trim();
    const chunk = note ? `${label} (${note})` : label;
    if (seen.has(chunk)) continue;
    seen.add(chunk);
    parts.push(chunk);
  }
  const joined = parts.join("; ");
  if (!joined) return "No floor issues recorded for this serial";
  return joined.length > 180 ? `${joined.slice(0, 177)}…` : joined;
}

export function buildConsumptionReport(
  units: readonly ConsumptionUnitInput[],
  issues: readonly ConsumptionIssueInput[],
  period_start: string,
  period_end: string
): ConsumptionReport {
  const byUnit = new Map<string, ConsumptionIssueInput[]>();
  for (const issue of issues) {
    if (!issue.mfg_unit_id) continue;
    const list = byUnit.get(issue.mfg_unit_id) ?? [];
    list.push(issue);
    byUnit.set(issue.mfg_unit_id, list);
  }

  const rows: ConsumptionSerialRow[] = units.map((u) => {
    const unitIssues = byUnit.get(u.id) ?? [];
    const materials_usd = roundUsd(
      unitIssues.reduce((s, i) => s + (Number(i.extended_cost_usd) || 0), 0)
    );
    return {
      unit_id: u.id,
      serial: serialLabel(u.serial, u.id),
      completed_on: completedOn(u.build_completed_at),
      materials_usd,
      why: whyFromIssues(unitIssues),
    };
  });
  rows.sort((a, b) => a.completed_on.localeCompare(b.completed_on) || a.serial.localeCompare(b.serial));

  return {
    period_start,
    period_end,
    serial_count: rows.length,
    total_usd: roundUsd(rows.reduce((s, r) => s + r.materials_usd, 0)),
    rows,
  };
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function consumptionReportCsv(report: ConsumptionReport): string {
  const rows = [
    ["Period start", report.period_start],
    ["Period end", report.period_end],
    ["Finished serials", String(report.serial_count)],
    ["Materials consumed USD", report.total_usd.toFixed(2)],
    [],
    ["Serial", "Completed", "Materials USD", "Why"],
    ...report.rows.map((r) => [
      r.serial,
      r.completed_on,
      r.materials_usd.toFixed(2),
      r.why,
    ]),
  ];
  return rows.map((r) => r.map(csvCell).join(",")).join("\n");
}
